import React, { useRef, useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PartGroup } from '../types/ServiceRequest';
import { useScrollPrevention } from '../hooks/useScrollPrevention';
import { useModalBackHandler } from '../hooks/useModalBackHandler';
import { useRipple } from '../hooks/useRipple';

interface PartGroupSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (group: PartGroup) => void;
  groups: PartGroup[];
  searchTerm: string;
  onSearchChange: (value: string) => void;
  selectedGroupId?: number;
  loadingGroupId?: number | null;
  fadeOnClose?: boolean;
}

const PartGroupSelector: React.FC<PartGroupSelectorProps> = ({
  isOpen,
  onClose,
  onSelect,
  groups,
  searchTerm,
  onSearchChange,
  selectedGroupId,
  loadingGroupId,
  fadeOnClose = false
}) => {


  // Scroll engelleyici hook kullan
  useScrollPrevention(isOpen);
  
  const selectedGroupRef = useRef<HTMLButtonElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Hover efektlerini kontrol etmek için
  const [allowHover, setAllowHover] = useState(false);
  
  // Modal kapatma animasyonu için
  const [isClosingWithFade, setIsClosingWithFade] = useState(false);
  
  // Ripple hook
  const { ripples, createRipple } = useRipple<number>();

  // Modal geri düğmesi handler
  const handleModalClose = useCallback(() => {
    onClose();
  }, [onClose]);
  
  useModalBackHandler({ 
    isOpen, 
    onClose: handleModalClose, 
    enabled: true
  });
  
  // Modal açıldığında hover efektlerini kısa süre devre dışı bırak
  useEffect(() => {
    if (isOpen) {
      setAllowHover(false);
      const timer = setTimeout(() => {
        setAllowHover(true);
      }, 500);
      
      return () => clearTimeout(timer);
    } else {
      setAllowHover(false);
    }
  }, [isOpen]);



  // Seçili öğeye scroll
  useEffect(() => {
    if (isOpen && selectedGroupId && scrollContainerRef.current && groups.length > 0) {
      const scrollTimer = setTimeout(() => {
        const filteredGroups = groups.filter(
          group => group.Ad.toLowerCase().includes(searchTerm.toLowerCase())
        );
        
        const selectedIndex = filteredGroups.findIndex(group => group.Id === selectedGroupId);
        
        if (selectedIndex !== -1) {
          // py-3 = 12px top + 12px bottom + text-lg content ≈ 56px
          // space-y-2 = 8px gap between items
          const buttonHeight = 56; // Actual button height
          const gapHeight = 8; // space-y-2 gap
          const itemTotalHeight = buttonHeight + gapHeight;
          
          const containerHeight = scrollContainerRef.current!.clientHeight;
          
          // Seçili item'i container'ın ortasına getir
          const itemPosition = selectedIndex * itemTotalHeight;
          const scrollTop = itemPosition - (containerHeight / 2) + (buttonHeight / 2);
          
          scrollContainerRef.current!.scrollTo({
            top: Math.max(0, scrollTop),
            behavior: 'smooth'
          });
        }
      }, 400);
      
      return () => clearTimeout(scrollTimer);
    }
  }, [isOpen, selectedGroupId, groups, searchTerm]);
  
  // Arama terimine göre filtrelenmiş parça grupları
  const filteredGroups = groups.filter(
    group => group.Ad.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          className="fixed inset-0 z-50 flex flex-col justify-end md:justify-center bg-black/20 dark:bg-black/40 backdrop-blur-sm modal-backdrop-optimized"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={fadeOnClose ? { opacity: 0, scale: 0.95 } : { y: '100%' }}
            transition={{ 
              type: 'tween', 
              ease: [0.32, 0.72, 0, 1], // Custom easing for better performance
              duration: fadeOnClose ? 0.18 : 0.25,
              opacity: { duration: 0.2 }
            }}
            className="w-full max-w-md mx-auto border border-gray-200 dark:border-gray-700 rounded-t-2xl md:rounded-2xl bg-white dark:bg-gray-800 shadow-2xl flex flex-col max-h-[70vh] md:max-h-[75vh] modal-optimized"
            style={{ 
              minHeight: 320,
              willChange: 'transform, opacity',
              transform: 'translateZ(0)' // Force GPU acceleration
            }}
            onClick={e => e.stopPropagation()}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={0.2}
            onDragEnd={(event, info) => {
              // Sadece aşağıya doğru 150px'den fazla kaydırıldığında kapat
              if (info.offset.y > 150) {
                onClose();
              }
            }}
          >
            <div className="rounded-t-2xl md:rounded-2xl p-4 pt-6 border border-white dark:border-gray-700 flex flex-col h-full">
            {/* Üst kısım ve arama kutusu sabit */}
            <div className="flex flex-col mb-2 flex-shrink-0 cursor-grab active:cursor-grabbing">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
              </div>
              <div className="relative flex items-center">
                <div className="w-full mb-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700">
                  <input
                    type="text"
                    className="w-full pr-10 text-base bg-transparent border border-white dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition duration-150 ease-in-out"
                    placeholder="Ara..."
                    value={searchTerm}
                    onChange={e => onSearchChange(e.target.value)}
                  />
                </div>
                {searchTerm && (
                  <button
                    type="button"
                    className="absolute right-3 mb-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 focus:outline-none flex items-center"
                    style={{ top: 0, bottom: 0 }}
                    onClick={() => onSearchChange('')}
                    tabIndex={-1}
                    aria-label="Temizle"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            
            {/* Liste kayar */}
            <div 
              ref={scrollContainerRef}
              className="space-y-2 overflow-y-auto max-h-[calc(70vh-80px)] md:max-h-[calc(75vh-80px)]"
              data-modal-scrollable
              onPointerDownCapture={(e) => e.stopPropagation()}
            >
              {filteredGroups.length === 0 ? (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 h-full min-h-[180px]">
                  <div className="rounded-lg border border-white dark:border-gray-700 p-6 h-full flex flex-col justify-center items-center text-center">
                  <div className="bg-gradient-to-br from-gray-100 via-slate-100 to-gray-200 dark:from-gray-600 dark:via-gray-600/90 dark:to-gray-500 border border-gray-200 dark:border-gray-500 rounded-full p-4 mb-4">
                    <svg className="w-16 h-16 text-gray-400 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <p className="text-gray-500 dark:text-gray-400">
                    Uygun parça grubu bulunamadı.
                  </p>
                  {searchTerm && (
                    <button
                      className="mt-4 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-semibold"
                      onClick={() => onSearchChange('')}
                    >
                      Aramayı Temizle
                    </button>
                  )}
                  </div>
                </div>
              ) : (
                filteredGroups.map((group) => (
                  <button
                    key={group.Id}
                    ref={selectedGroupId === group.Id ? selectedGroupRef : undefined}
                    type="button"
                    className={`relative overflow-hidden w-full rounded-lg transition-all duration-400 ease-in-out
                      ${selectedGroupId === group.Id
                        ? 'border border-indigo-200 dark:border-indigo-700 bg-gradient-to-br from-indigo-100 via-blue-100 to-indigo-200 dark:from-indigo-900/30 dark:via-blue-900/25 dark:to-indigo-800/30'
                        : `border border-gray-300 dark:border-gray-600 bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 dark:from-gray-700/50 dark:via-gray-700/70 dark:to-gray-700/50 ${allowHover ? 'hover:from-gray-150 hover:via-gray-200 hover:to-gray-150 dark:hover:from-gray-600/80 dark:hover:via-gray-600 dark:hover:to-gray-600/80 hover:border-gray-400 dark:hover:border-gray-500' : ''}`}
                    `}
                    onClick={(e) => loadingGroupId !== group.Id ? createRipple(e, group.Id, () => onSelect(group), 100) : undefined}
                    disabled={loadingGroupId === group.Id}
                  >
                    <div className={`rounded-lg border border-white dark:border-gray-700 px-4 py-3 flex items-center text-lg font-medium
                      ${selectedGroupId === group.Id
                        ? 'text-indigo-700 dark:text-indigo-300'
                        : 'text-gray-900 dark:text-white'}
                    `}>
                      <span className="flex-1 text-left relative z-10">{group.Ad}</span>
                      {loadingGroupId === group.Id ? (
                        <svg className="animate-spin w-5 h-5 text-indigo-600 dark:text-indigo-400 relative z-10" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 718-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : selectedGroupId === group.Id ? (
                        <svg className="w-6 h-6 text-indigo-600 dark:text-indigo-400 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : null}
                    </div>
                    
                    {/* Ripple Effects */}
                    {ripples[group.Id]?.map((ripple) => (
                      <span
                        key={ripple.id}
                        className="absolute rounded-full pointer-events-none"
                        style={{
                          left: ripple.x - (ripple.size || 0) / 2,
                          top: ripple.y - (ripple.size || 0) / 2,
                          width: ripple.size || 0,
                          height: ripple.size || 0,
                          backgroundColor: selectedGroupId === group.Id 
                            ? 'rgba(99, 102, 241, 0.3)' 
                            : 'rgba(156, 163, 175, 0.4)',
                          animation: 'ripple-modal 600ms ease-out forwards'
                        }}
                      />
                    ))}
                  </button>
                ))
              )}
            </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PartGroupSelector; 